import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { VehiclePositions as Podaci, VehicleTrace } from '../types';

/** Osvežava se češće od ostatka ekrana — pozicija stara pet minuta ne vredi. */
const REFRESH_MS = 60_000;

/**
 * Gde su vozila sada, po GPS uređajima iz Automonitora.
 *
 * Uređaji se sa vozilima spajaju RUČNO: njihove oznake nisu naše registracije,
 * a spisak uređaja sa nazivima kod njih ne radi. Zato spisak pokazuje i
 * nesparene uređaje sa poslednjom pozicijom — po njoj operater prepozna koje
 * je vozilo i spari ga.
 */
interface Props {
  /**
   * „pregled" je posao dana: gde su vozila sada i kuda su išla.
   * „setup" je sparivanje uređaja sa vozilima — radi se kad se uređaj zameni.
   */
  mode?: 'pregled' | 'setup';
}

export default function VehiclePositions({ mode = 'pregled' }: Props) {
  const [data, setData] = useState<Podaci | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  /** Otvoren dan jednog vozila — gde je bio i koliko se zadržao. */
  const [trace, setTrace] = useState<VehicleTrace | null>(null);
  const [traceFor, setTraceFor] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.vehiclePositions());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const pair = async (deviceId: string, vehicleId: number | null) => {
    setBusy(deviceId);
    try {
      if (vehicleId !== null) {
        await api.setVehicleGps(vehicleId, deviceId);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const unpair = async (vehicleId: number) => {
    setBusy(String(vehicleId));
    try {
      await api.setVehicleGps(vehicleId, '');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const openTrace = async (vehicleId: number) => {
    if (traceFor === vehicleId) {
      setTraceFor(null);
      setTrace(null);
      return;
    }

    setTraceFor(vehicleId);
    setTrace(null);
    try {
      setTrace(await api.vehicleTrace(vehicleId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const sat = (vreme: string) =>
    new Date(vreme).toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });

  const setup = mode === 'setup';

  // NIKAD NE NESTATI BEZ REČI.
  //
  // Do 30.09.2026. je ova komponenta vraćala `null` u tri slučaja — dok se učitava,
  // kad GPS nije podešen, i kad nijedno vozilo ne javlja poziciju. Sva tri su na
  // ekranu izgledala isto: odeljka jednostavno nema. Vlasnik je 30.09. prijavio
  // „nema vozila ni dole", a ekran mu nije rekao ni da nešto nedostaje, ni šta.
  //
  // Prazno stanje je nalaz, ne odsustvo nalaza — zato svaki od tih slučajeva sada
  // kaže šta je i šta se radi.
  const prazno = (poruka: React.ReactNode) => (
    <section className="panel">
      <h2>{setup ? 'GPS uređaji' : 'Gde su vozila'}</h2>
      {error ? <div className="health health--bad">{error}</div> : <p className="note">{poruka}</p>}
    </section>
  );

  if (!data) {
    return prazno('Učitavam pozicije…');
  }

  if (!data.configured) {
    return prazno(
      'GPS praćenje nije podešeno na serveru — nedostaju Tracking__Username i '
      + 'Tracking__Password u web.config-u. Dok ih nema, pozicija nema odakle da stigne.',
    );
  }

  const shown = setup
    ? data.positions
    // U pregledu dana nemaju šta da traže tuđi uređaji: operatera zanimaju
    // NAŠA vozila, a nespareni uređaji su posao za podešavanja.
    : data.positions.filter((p) => p.vehicleId !== null);

  if (shown.length === 0) {
    // Tri različita stanja koja na ekranu izgledaju isto. Razlika štedi istragu:
    // očitavanje je palo, nijedan uređaj se ne javlja, ili se javljaju a nijedan
    // nije sparen. Prvo od njih zna i ZAŠTO — vidi `IVehicleTracker.LastFailure`.
    if (data.failure) {
      return prazno(
        <>
          <strong>Očitavanje pozicija ne prolazi.</strong> {data.failure}
        </>,
      );
    }

    return prazno(
      data.positions.length === 0
        ? 'Nijedan GPS uređaj se trenutno ne javlja, a provajder ne prijavljuje '
          + 'grešku. Provera je automatska svakog minuta.'
        : `${data.positions.length} uređaja javlja poziciju, ali nijedan nije sparen `
          + 'sa vozilom. Sparivanje se radi u Podešavanjima — uređaj se prepoznaje '
          + 'po mestu na kom stoji.',
    );
  }

  return (
    <section className="panel">
      <h2>
        {setup ? 'GPS uređaji' : 'Gde su vozila'}
        <span className="muted"> · {shown.length}</span>
        {!setup && shown.some((p) => p.isMoving) && (
          <span className="flag flag--ok">
            {shown.filter((p) => p.isMoving).length} u vožnji
          </span>
        )}
      </h2>

      <p className="note">
        {setup
          ? 'Uređaj koji nije sparen prepoznaje se po mestu na kom stoji — '
            + 'izaberi vozilo iz spiska i sparivanje se pamti.'
          : 'Poslednje javljanje GPS uređaja. Klik na „Dan" pokazuje gde je '
            + 'vozilo bilo i koliko se zadržalo.'}
      </p>

      {error && <div className="health health--bad">{error}</div>}

      {/* Veza bez šifrovanja je svestan ustupak — Automonitor je 30.09.2026. sklonio
          TLS sa svog porta, pa lozinka putuje otvoreno. Privremeno rešenje koje niko
          ne vidi postaje trajno, zato stoji na ekranu dok traje. */}
      {data.encrypted === false && (
        <div className="health">
          Veza sa Automonitorom nije šifrovana — privremeno, dok ne vrate HTTPS.
          Lozinka putuje otvoreno.
        </div>
      )}

      {/* Pozicije mogu biti stare: poslednje uspelo očitavanje je prošlo, a ovo
          nije. Bez ove rečenice bi ekran pokazivao jučerašnje stanje kao današnje. */}
      {data.failure && (
        <div className="health health--bad">
          Prikazano je poslednje poznato stanje. {data.failure}
        </div>
      )}

      <ul className="storelist">
        {shown.map((position) => (
          <li key={position.deviceId}>
            <span className="storelist__where">
              {position.vehicleName ?? <em>nesparen uređaj {position.deviceId}</em>}
              {position.registration && <span className="muted"> · {position.registration}</span>}
              {position.tourName && (
                <span className="muted"> · tura {position.tourName}
                  {position.driver && ` · ${position.driver}`}
                </span>
              )}
              <span className="storelist__why muted">
                {position.isMoving
                  ? `u vožnji, ${position.speedKmh.toFixed(0)} km/h`
                  : 'stoji'}
                {position.nearPlace && (
                  <>
                    {' · '}
                    {position.nearKm !== null && position.nearKm < 0.5
                      ? <strong>kod {position.nearPlace}</strong>
                      : <>{position.nearKm?.toFixed(1)} km od {position.nearPlace}</>}
                  </>
                )}
                {' · javio se pre '}
                {position.ageMinutes < 60
                  ? `${position.ageMinutes} min`
                  : `${Math.round(position.ageMinutes / 60)} h`}
                {' · '}
                <a
                  href={`https://www.google.com/maps?q=${position.latitude},${position.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  na mapi
                </a>
              </span>
            </span>

            {position.vehicleId === null && setup ? (
              <select
                value=""
                disabled={busy === position.deviceId}
                onChange={(e) => void pair(position.deviceId, Number(e.target.value) || null)}
                aria-label={`Vozilo za uređaj ${position.deviceId}`}
              >
                <option value="">spari sa vozilom…</option>
                {data.vehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.name} · {vehicle.registration}
                    {vehicle.gpsDeviceId ? ` (sad ${vehicle.gpsDeviceId})` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <span className="stop__actions">
                <span className={position.isMoving ? 'flag flag--ok' : 'flag'}>
                  {position.deviceId}
                </span>
                {!setup && (
                  <button
                    type="button"
                    onClick={() => void openTrace(position.vehicleId!)}
                    title="Gde je bio danas"
                  >
                    {traceFor === position.vehicleId ? 'Zatvori' : 'Dan'}
                  </button>
                )}
                {setup && (
                  <button
                    type="button"
                    disabled={busy === String(position.vehicleId)}
                    onClick={() => void unpair(position.vehicleId!)}
                    title="Raskini vezu sa vozilom"
                  >
                    ×
                  </button>
                )}
              </span>
            )}

            {traceFor === position.vehicleId && (
              <div className="stop__cargo">
                {!trace ? (
                  <p className="muted">Učitavam…</p>
                ) : trace.stops.length === 0 && trace.samples === 0 ? (
                  <p className="muted">
                    Za ovaj dan nema zabeleženih pozicija. Beleženje radi otkad
                    je aplikacija podešena — raniji dani se ne mogu vratiti.
                  </p>
                ) : (
                  <>
                    <p className="muted">
                      {trace.samples} zapisa
                      {trace.firstSeen && <> · od {sat(trace.firstSeen)}</>}
                      {trace.lastSeen && <> do {sat(trace.lastSeen)}</>}
                      {trace.atLeastKm > 0 && <> · najmanje {trace.atLeastKm.toFixed(0)} km</>}
                    </p>

                    {trace.stops.length === 0 ? (
                      <p className="muted">Nijedno zadržavanje duže od tri minuta.</p>
                    ) : (
                      <ol className="legs">
                        {trace.stops.map((stop) => (
                          <li key={stop.from}>
                            <strong>{sat(stop.from)}–{sat(stop.to)}</strong>
                            {' · '}{stop.minutes} min{' · '}
                            {stop.place
                              ? (stop.distanceKm !== null && stop.distanceKm < 0.5
                                ? stop.place
                                : `${stop.distanceKm?.toFixed(1)} km od ${stop.place}`)
                              : <a
                                  href={`https://www.google.com/maps?q=${stop.latitude},${stop.longitude}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >van naše mreže</a>}
                          </li>
                        ))}
                      </ol>
                    )}
                  </>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
